/**
 * Витринный контракт тарифа (/api/v1/tariffs) + хелперы подачи.
 *
 * Вынесено из pages/Tariffs.tsx (ступень 2 переиспользования карточки,
 * 2026-08-07): типы и чистые функции текстов нужны и витрине, и ЛК
 * (TariffCardView в биллинге показывает купленный тариф тем же движком).
 * Логика здесь ПРЕЗЕНТАЦИОННАЯ — никаких запросов и стейта.
 */

export interface TariffItem {
	item_type: string
	adapter_name: string | null
	model_id: string | null
	custom_description: string | null
	credits_override: number | null
	is_enabled: boolean
	/** Кому видна строка: any | subscription | one_time */
	applies_to?: string
}

export interface TariffPack {
	id: string
	name: string
	description: string | null
}

export interface FreeModel {
	name: string
	daily_limit: number
	params_label: string | null
	applies_to?: 'any' | 'subscription' | 'one_time'
}

export interface ModelDiscount {
	name: string
	percent: number
	applies_to?: 'any' | 'subscription' | 'one_time'
}

export interface IncludedGroup {
	category: string
	names: string[]
}

export interface EnoughCell {
	label: string
	count: number
	/** Модель-эталон ячейки — ховер показывает иконку бренда и имя модели
	 *  (пусто у эталонов с ручной ценой без привязки к модели) */
	adapter_name?: string | null
	model_id?: string | null
	model_name?: string | null
}

export interface CardBlock {
	kind: 'enough' | 'free' | 'perks' | 'included' | 'custom'
	title: string | null
	/** Кому виден блок: any | subscription | one_time */
	applies_to?: string
	payload: {
		style?: 'violet' | 'mint' | 'plain'
		layout?: 'plus' | 'rows'
		/** Маркер строк бокса (layout 'plus'); цвет — от тинта бокса.
		 *  Отсутствует/неизвестен = 'plus' (легаси-боксы) */
		marker?: 'plus' | 'dot' | 'check' | 'sparkle' | 'arrow' | 'none'
		collapsed?: boolean
		/** Системные авто-строки (текст считается из настроек тарифа, свой
		 *  шаблон — опционально в title/text):
		 *  'carryover' — перенос токенов, подстановка {pct};
		 *  'characters' — персонажи, подстановки {limit}/{free}; значения
		 *  ветвятся по подаче (подписка / разово) автоматически, а otTitle/
		 *  otText — свои шаблоны РАЗОВОЙ подачи (пусто = как title/text).
		 *  marker строки перебивает маркер бокса (нет = как у бокса) */
		lines?: { title?: string; text?: string; otTitle?: string; otText?: string; kind?: 'text' | 'carryover' | 'characters'; marker?: 'plus' | 'dot' | 'check' | 'sparkle' | 'arrow' | 'none' }[]
	} | null
}

export interface Tariff {
	id: string
	name: string
	description: string | null
	price: number
	currency: string
	credits: number
	/** Процент переноса остатка при продлении подписки (0–100) */
	carryover_percent?: number
	items: TariffItem[]
	// Витрина-2026: все поля опциональны (старый бэк их не отдаёт) и пустой
	// список = блок не рендерится. Данные приходят ровно из админ-настроек.
	free_models?: FreeModel[]
	model_discounts?: ModelDiscount[]
	character_limit?: number | null
	character_free_limit?: number | null
	/** Промо-бейдж из конструктора тарифа; пусто у всех = легаси-хардкод */
	promo_badge?: string | null
	/** Вариация бонус-пака (в выдаче только у суперадмина и у владельца) */
	pack_id?: string | null
	base_tariff_id?: string | null
	/** Период тарифа в днях — из него считается подпись цены */
	token_expire_days?: number | null
	/** Режим продажи: both | subscription | one_time */
	purchase_mode?: 'both' | 'subscription' | 'one_time'
	character_limit_one_time?: number | null
	character_free_limit_one_time?: number | null
	/** Токены разовой покупки (null = как credits) */
	credits_one_time?: number | null
	included?: IncludedGroup[]
	enough?: EnoughCell[]
	card_blocks?: CardBlock[]
}

// Заголовки категорий блока «Какие модели входят» (ключи model_intent бэка)
export const INCLUDED_CATEGORY_LABELS: Record<string, string> = {
	video: 'Видео',
	image: 'Изображения',
	text: 'Текст',
	audio: 'Аудио',
	presentation: 'Презентации',
	other: 'Другое',
}

// Персонажи (двойники из фото) — строка-фича из настроек тарифа. limit 0 =
// на тарифе персонажей нет, строку не показываем (без негатива на витрине).
export function characterFeature(limit?: number | null, free?: number | null): string | null {
	if (!limit || limit <= 0) return null
	const f = Math.min(free ?? 0, limit)
	const genitive = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 'аватара' : 'аватаров')
	const base = limit === 1 ? '**1 аватар** из ваших фото' : `**До ${limit} ${genitive(limit)}** из ваших фото`
	if (f <= 0) return base
	if (f >= limit) return `${base} — бесплатно`
	if (f === 1) return `${base}, первый — бесплатно`
	return `${base}, первые ${f} — бесплатно`
}

// «Какие модели входят»: правая колонка сжимается до «Имя1, Имя2 + N моделей»
export function includedShort(names: string[]): string {
	if (names.length <= 3) return names.join(', ')
	return `${names.slice(0, 2).join(', ')} + ${names.length - 2} моделей`
}

// Скидки строками «−15%: Flux Pro, Banana 2» — группировка по проценту
export function discountLines(discounts: ModelDiscount[] | undefined): string[] {
	if (!discounts?.length) return []
	const byPercent = new Map<number, string[]>()
	for (const d of discounts) {
		const list = byPercent.get(d.percent)
		if (list) list.push(d.name)
		else byPercent.set(d.percent, [d.name])
	}
	return [...byPercent.entries()]
		.sort((a, b) => b[0] - a[0])
		.map(([pct, names]) => `−${pct}% токенов: ${names.join(', ')}`)
}

// Перенос остатка: строка-выгода только когда перенос реально есть (>0)
export function carryoverBenefit(t: Tariff): string | null {
	const c = t.carryover_percent ?? 0
	if (c >= 100) return 'Токены не сгорают — остаток переносится'
	if (c > 0) return `${c}% остатка переносится на следующий период`
	return null
}

export function formatPrice(price: number): string {
	if (price === 0) return '0₽'
	return price.toLocaleString('ru-RU') + '₽'
}

// Подпись периода из НАСТОЯЩЕГО периода тарифа: 28–31 день — «/ мес»
export function periodLabel(days?: number | null): string {
	const d = days ?? 30
	if (d >= 28 && d <= 31) return '/ мес'
	const genitive = d % 10 === 1 && d % 100 !== 11
		? 'день'
		: d % 10 >= 2 && d % 10 <= 4 && (d % 100 < 12 || d % 100 > 14)
			? 'дня'
			: 'дней'
	return `/ ${d} ${genitive}`
}

export function formatCredits(credits: number): string {
	if (credits === 0) return '0'
	return credits.toLocaleString('ru-RU')
}

// Описание тарифа: первая простая строка — подзаголовок, «—»-строки — перки
export function parseDescription(desc: string | null): { hint: string; features: string[] } {
	if (!desc) return { hint: '', features: [] }
	const lines = desc.split('\n').map(l => l.trim()).filter(Boolean)
	const features: string[] = []
	let hint = ''
	for (const line of lines) {
		if (/^[—\-–•]/.test(line)) {
			features.push(line.replace(/^[—\-–•]\s*/, ''))
		} else if (!hint) {
			hint = line
		}
	}
	return { hint, features }
}
